/** MUI theme, built from the infiniwell brand reference
 *  (`brand-template.html`, "theme migration spec").
 *
 *  The ramps below are transcribed from that document rather than sampled from
 *  a rendering of it, so a future revision of the brand can be diffed against
 *  this file directly. Roles resolve to ramp steps by the spec's own mapping -
 *  `primary.main` is `primary.500`, not a nearby blue that looked right.
 *
 *  Two rules from the spec are load-bearing here, because breaking them is
 *  invisible until it matters:
 *
 *  - **Amber is `warning` and nothing else.** It is not a brand colour, and a
 *    decorative amber makes a real warning unreadable as one.
 *  - **Destructive is red `#CB3A3A`, never coral.** Coral is the brand accent;
 *    hue-separating the two is what keeps "delete" from reading as "branded".
 *
 *  That sits on top of this screen's own rule, which predates the brand work
 *  and still holds: semantic colour is reserved for review status and MDR
 *  tier, so nothing decorative may use success/warning/error.
 *
 *  Ported verbatim from mission-agent/frontend/dashboard/src/theme and applied
 *  to the Mission Agent page only (nested ThemeProvider), so the rest of
 *  sentioweb keeps its own theme.
 */

import { createTheme } from "@mui/material/styles";

/** Steel blue. 500 is the brand anchor: primary actions, links, active nav. */
const primary = {
  50: "#EEF3FA",
  100: "#D6E2F1",
  200: "#AEC4E2",
  300: "#85A5D2",
  400: "#5D87C3",
  500: "#3E6DAF",
  600: "#35619E",
  700: "#2C5082",
  800: "#233F66",
  900: "#1A2E4A",
} as const;

/** Clay / coral - a terracotta, not an amber. 500 is the signature accent;
 *  600 is the step that carries white text at AA. */
const accent = {
  50: "#FBEEE8",
  100: "#F5D9CB",
  300: "#E5A488",
  500: "#D97757",
  600: "#C15F3C",
  700: "#9A4A2C",
} as const;

/** Warm cream through ink. 100 is divider, 500 is secondary text. */
const neutral = {
  0: "#FFFFFF",
  25: "#FAF9F5",
  50: "#F5F3EC",
  100: "#EBE8DF",
  200: "#DAD5C8",
  300: "#BCB5A6",
  400: "#928B7C",
  500: "#6B6459",
  600: "#4A453D",
  700: "#33302B",
  800: "#262421",
  850: "#1F1E1B",
  900: "#171614",
  950: "#0F0E0C",
} as const;

/** Light-mode role tokens, per the spec's role mapping table. The dark column
 *  of that table is deliberately not transcribed: this console renders light
 *  only, and an unreachable palette would drift out of step with the brand
 *  unnoticed. Add it here, from the spec, if a mode toggle ever lands. */
const role = {
  bg: neutral[25],
  paper: neutral[0],
  divider: neutral[100],
  text: "#1A1815",
  text2: neutral[500],
  text3: "#A69F92",
} as const;

/** The spec's semantic tints: pale fills for status surfaces (a soft button, a
 *  banner, a status cell) where the solid `main` would be too loud. Kept out
 *  of the MUI palette because MUI's `light` slot means "a lighter shade of the
 *  same role", which it derives, and overriding it with a tint breaks that. */
export const tint = {
  success: "#E7F3EA",
  warning: "#FBEFDA",
  error: "#F8E7E7",
  info: "#E7F0FA",
  accent: accent[50],
} as const;

/** The brand ramps, for the rare case a component needs a specific step rather
 *  than a semantic role (a chart series, a diagram fill). Prefer the palette. */
export const ramp = { primary, accent, neutral } as const;

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: primary[500],
      dark: primary[600], // hover
      light: primary[400],
      contrastText: neutral[0],
    },
    // Coral carries secondary emphasis - one hero accent per view, per the
    // spec. 600 rather than the brighter 500 because this is the step that
    // holds white text at AA.
    secondary: {
      main: accent[600],
      light: accent[500],
      dark: accent[700],
      contrastText: neutral[0],
    },
    // Only `main` is set from the spec. MUI derives `light`/`dark` from it and
    // uses `light` to compute Alert backgrounds and hover states, so putting
    // the spec's pale tints in that slot would wash an Alert out to near-white
    // and drop its text contrast. The tints are surface colours in their own
    // right - see `tint` below.
    success: { main: "#2E9E5B", contrastText: neutral[0] },
    warning: { main: "#E0902A", contrastText: neutral[0] },
    error: { main: "#CB3A3A", contrastText: neutral[0] },
    info: { main: "#3E82C4", contrastText: neutral[0] },
    background: { default: role.bg, paper: role.paper },
    text: { primary: role.text, secondary: role.text2, disabled: role.text3 },
    divider: role.divider,
  },
  // 8px is the spec's default radius (buttons, inputs, tags); cards and panels
  // take 12px, applied on MuiPaper below.
  shape: { borderRadius: 8 },
  spacing: 8,
  typography: {
    fontFamily:
      '"Inter","Roboto",-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif',
    // The spec's display scale, stepped down for a dense operations screen:
    // its H1 is 56px for a marketing surface, which would dwarf a table of
    // mission ids. Stepped down again to the dovre apps' density - their
    // workhorse step is 13px (inputs, labels, menu items, list text), with
    // 14px body and 12px quiet meta. Weights, tracking and case follow the
    // spec as given.
    h1: { fontSize: "1.25rem", fontWeight: 300, letterSpacing: "-0.015em" },
    h2: { fontSize: "1rem", fontWeight: 400, letterSpacing: "-0.005em" },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 600 },
    body1: { fontSize: "0.875rem" },
    body2: { fontSize: "0.8125rem" },
    button: {
      fontSize: "0.8125rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    },
    overline: {
      letterSpacing: "0.16em",
      fontWeight: 600,
      fontSize: "0.68rem",
      textTransform: "uppercase",
    },
    caption: { fontSize: "0.75rem", letterSpacing: "0.033em" },
  },
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${role.divider}`,
          borderRadius: 12,
        },
      },
    },
    // Chips are the spec's pill shape, and this screen uses a lot of them.
    MuiChip: { styleOverrides: { root: { borderRadius: 999 } } },
    // Dense buttons, per the dovre apps' theme: default to `small` and tighten
    // padding so a row of actions stops towering over the text beside it. The
    // brand's uppercase voice from `typography.button` above still applies -
    // dovre zeroes its letter tracking, but this brand keeps the spec's .06em.
    MuiButton: {
      defaultProps: { disableElevation: true, size: "small" },
      styleOverrides: {
        root: {
          paddingTop: 4,
          paddingBottom: 4,
          paddingInline: 12,
          minHeight: 30,
          minWidth: 0,
          lineHeight: 1.4,
        },
        sizeSmall: {
          paddingTop: 2,
          paddingBottom: 2,
          paddingInline: 10,
          minHeight: 26,
        },
        sizeLarge: {
          paddingTop: 6,
          paddingBottom: 6,
          paddingInline: 18,
          minHeight: 38,
          fontSize: "0.9375rem",
        },
      },
    },
    // One size for every field's typed-or-chosen value (dovre convention): 13px
    // with a unitless line height so the box shrinks with the text. A Select's
    // closed value renders through this same slot, so it stays in step for
    // free - centralise the size here rather than patching each field.
    MuiOutlinedInput: {
      styleOverrides: { input: { fontSize: "0.8125rem", lineHeight: 1.4375 } },
    },
    // Match the value size, so the floating label never reads larger than the
    // text it is labelling once a value is entered.
    MuiInputLabel: { styleOverrides: { root: { fontSize: "0.8125rem" } } },
    // Dense menu choices, for any select that lands later: short 13px rows.
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 32,
          paddingTop: 4,
          paddingBottom: 4,
          fontSize: "0.8125rem",
        },
      },
    },
  },
});
